import React, { useEffect, useRef, useState } from 'react';
import client from '../api/client';
import { toastError } from '../toastHelper';
import ComparableEventsChart from './ComparableEventsChart';
import ComparableEventsTable from './ComparableEventsTable';
import ComparableEventsEditor from './ComparableEventsEditor';
import './marketingShared.css';
import './ComparableEvents.css';

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mi-ce-select-chevron">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SIMILAR_PREF_KEY = 'marketing:comparableEvents:autoSuggestSimilar';
const readSimilarPref = () => {
  try {
    return window.localStorage.getItem(SIMILAR_PREF_KEY) !== 'off';
  } catch (e) {
    return true;
  }
};
const writeSimilarPref = (on) => {
  try {
    window.localStorage.setItem(SIMILAR_PREF_KEY, on ? 'on' : 'off');
  } catch (e) {
    return;
  }
};

const ComparableEvents = ({ teamId, confirmId, data: dataProp, sectionNumber }) => {
  const hasInjectedData = dataProp !== undefined;
  const shouldFetch = !hasInjectedData && Boolean(teamId) && Boolean(confirmId);

  const baseUrl = `/teams/${teamId}/confirms/${confirmId}`;

  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(shouldFetch);
  const [busy, setBusy] = useState(false);
  const [product, setProduct] = useState('tickets');
  const [timing, setTiming] = useState('all');
  const [mode, setMode] = useState('cumulative');
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!shouldFetch || !teamId || !confirmId) {
      if (shouldFetch) setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    client
      .get(`${baseUrl}/confirm_comparisons`, { signal: controller.signal })
      .then((response) => setData(response.data))
      .catch((err) => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        toastError('Unable to load comparable events.');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [shouldFetch, teamId, confirmId]);

  const mutate = async (request) => {
    setBusy(true);
    try {
      const result = await request();
      if (result?.data?.table !== undefined) {
        setData(result.data);
      } else {
        const response = await client.get(`${baseUrl}/confirm_comparisons`);
        setData(response.data);
      }
    } catch (err) {
      const messages = err.response?.data?.errors;
      toastError(messages ? messages.join(', ') : 'Unable to update comparable events.');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (id) => mutate(() =>
    client.post(`${baseUrl}/confirm_comparisons`, { confirm_comparison: { compared_calendar_event_id: id } }));
  const handleRemove = (comparisonId) => mutate(() =>
    client.delete(`${baseUrl}/confirm_comparisons/${comparisonId}`));
  const handleToggleSimilar = (on) => {
    writeSimilarPref(on);
    return mutate(() =>
      (on
        ? client.post(`${baseUrl}/confirm_comparisons/similar`)
        : client.delete(`${baseUrl}/confirm_comparisons/similar`)));
  };

  const reconciledForRef = useRef(null);
  useEffect(() => {
    if (!data || busy) return;
    if (reconciledForRef.current === confirmId) return;
    reconciledForRef.current = confirmId;
    if (data.similar_on && !readSimilarPref()) {
      handleToggleSimilar(false);
    }
  }, [data, busy, confirmId]);

  const comps = data?.comps || [];
  const stats = data?.subject_stats || {};

  return (
    <div className="mi-ce-card">
      <div className="mi-ce-header">
        <div className="mi-ce-header-text">
          <div className="mi-ce-title">Comparable events</div>
          <div className="mi-ce-subtitle">How this event is pacing against similar past shows</div>
        </div>
        {!loading && data && (
          <div className="mi-ce-controls">
              <div className="mi-ce-segmented" role="group" aria-label="Cumulative or daily">
                <button
                  type="button"
                  className={mode === 'cumulative' ? 'is-active' : ''}
                  onClick={() => setMode('cumulative')}
                >
                  Total
                </button>
                <button
                  type="button"
                  className={mode === 'daily' ? 'is-active' : ''}
                  onClick={() => setMode('daily')}
                >
                  Daily
                </button>
              </div>
              <div className="mi-ce-select-box">
                <span className="mi-ce-select-box-label">Metric</span>
                <select
                  className="mi-ce-native-select"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  aria-label="Metric"
                >
                  <option value="tickets">Tickets</option>
                  <option value="add_ons">Add-Ons</option>
                </select>
                <ChevronIcon />
              </div>
              <div className="mi-ce-select-box">
                <select
                  className="mi-ce-native-select"
                  value={timing}
                  onChange={(e) => setTiming(e.target.value)}
                  aria-label="Ticket timing"
                >
                  <option value="all">All tickets</option>
                  <option value="advance">Advance</option>
                  <option value="dos">DOS</option>
                </select>
                <ChevronIcon />
              </div>
            </div>
        )}
      </div>

      {loading && <div className="mi-ce-empty-panel">Loading comparable events…</div>}
      {!loading && data && (
        <>
          <div className="mi-ce-body">
            <div className="mi-ce-kpis">
              <div className="mi-ce-kpi">
                <div className="mi-ce-kpi-label">Sold to date</div>
                <div className="mi-ce-kpi-value-row">
                  <span className="mi-ce-kpi-value">{(stats.sold || 0).toLocaleString()}</span>
                  <span className="mi-ce-kpi-sub">
                    {`of ${(stats.capacity || 0).toLocaleString()} · ${stats.capacity_pct ?? 0}%`}
                  </span>
                </div>
              </div>
              <div className="mi-ce-kpi-divider" />
              <div className="mi-ce-kpi">
                <div className="mi-ce-kpi-label">Days to show</div>
                <div className="mi-ce-kpi-value">{stats.days_to_show ?? 0}</div>
              </div>
            </div>

            <ComparableEventsChart
              subject={data.subject}
              comps={comps}
              chart={data.chart || []}
              projection={data.projection || []}
              currentDte={data.subject_current_dte}
              capacity={stats.capacity}
              product={product}
              timing={timing}
              mode={mode}
            />
          </div>

          <div className="mi-ce-footer">
            {comps.length > 0 ? (
              <>
                <div className="mi-ce-footer-head">
                  <div className="mi-ce-footer-title">
                    {`Comparing ${comps.length} past show${comps.length === 1 ? '' : 's'}`}
                    {busy && <span className="mi-ce-updating">Updating…</span>}
                  </div>
                  <div className="mi-ce-footer-actions">
                    <label className="mi-ce-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(data.similar_on)}
                        disabled={busy}
                        onChange={(e) => handleToggleSimilar(e.target.checked)}
                      />
                      <span className="mi-ce-checkbox-box"><CheckIcon /></span>
                      Auto-suggest similar events
                    </label>
                    <ComparableEventsEditor
                      availableEvents={data.available_events || []}
                      onAdd={handleAdd}
                      busy={busy}
                    />
                  </div>
                </div>
                <ComparableEventsTable
                  table={data.table || []}
                  currentDte={data.subject_current_dte}
                  comps={comps}
                  onRemove={handleRemove}
                  busy={busy}
                />
              </>
            ) : (
              <div className="mi-ce-empty">
                <div className="mi-ce-empty-text">
                  <div className="mi-ce-empty-title">No comps added yet</div>
                  <div className="mi-ce-empty-desc">
                    {`Add a past show to see whether ${(stats.sold || 0).toLocaleString()} at D-${stats.days_to_show ?? 0} is ahead or behind.`}
                  </div>
                </div>
                <div className="mi-ce-empty-actions">
                  {!showPicker && (
                    <>
                      <button type="button" className="mi-ce-btn-secondary" onClick={() => setShowPicker(true)} disabled={busy}>
                        Browse past shows
                      </button>
                      <button type="button" className="mi-ce-btn-primary" onClick={() => handleToggleSimilar(true)} disabled={busy}>
                        {busy ? 'Finding similar shows…' : <><PlusIcon />Suggest similar events</>}
                      </button>
                    </>
                  )}
                  {showPicker && (
                    <>
                      <ComparableEventsEditor
                        availableEvents={data.available_events || []}
                        onAdd={handleAdd}
                        busy={busy}
                        autoFocus
                      />
                      <button type="button" className="mi-ce-btn-secondary" onClick={() => setShowPicker(false)}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {!loading && !data && (
        <div className="mi-ce-empty-panel">No comparable events available.</div>
      )}
    </div>
  );
};

export default ComparableEvents;
